// Singapore residential property affordability — pure calculation functions.
// Rules applied (verified against MAS / IRAS sources, Nov 2026):
// - TDSR: 55% of gross monthly income covers ALL debt obligations (MAS, OTP on/after 16 Dec 2021)
//   https://www.mas.gov.sg/regulation/explainers/tdsr-for-property-loans/calculating-tdsr
// - Stress-test rate: higher of 4% p.a. medium-term floor or the thereafter rate (MAS Notice 645)
// - LTV (bank loan, private property): 1st loan 75%, 2nd 45%, 3rd+ 35%;
//   reduced to 55/25/15% when tenure > 30 years or loan extends past age 65 (MAS Notice 632/825)
//   https://www.mas.gov.sg/regulation/explainers/new-housing-loans/loan-tenure-and-loan-to-value-limits
// - Minimum cash downpayment: 5% (LTV 75%), 10% (LTV 55%), 25% (2nd and subsequent loans)
// - Max tenure for private property: 35 years
// - BSD residential tiers effective 15 Feb 2023; ABSD rates effective 27 Apr 2023 (iras.gov.sg)

export const TDSR_LIMIT = 0.55;
export const STRESS_RATE_FLOOR_PCT = 4;
export const MAX_TENURE_YEARS = 35;
export const MAX_LOAN_AGE = 75;

const clean = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

export function monthlyPayment(principal, annualRatePct, years) {
  const P = clean(principal);
  const n = Math.round(clean(years) * 12);
  const r = clean(annualRatePct) / 100 / 12;
  if (P === 0 || n === 0) return 0;
  if (r === 0) return P / n;
  const f = Math.pow(1 + r, n);
  return (P * r * f) / (f - 1);
}

export function loanFromPayment(monthly, annualRatePct, years) {
  const M = clean(monthly);
  const n = Math.round(clean(years) * 12);
  const r = clean(annualRatePct) / 100 / 12;
  if (M === 0 || n === 0) return 0;
  if (r === 0) return M * n;
  const f = Math.pow(1 + r, n);
  return (M * (f - 1)) / (r * f);
}

const BSD_TIERS = [
  { upto: 180000, rate: 0.01 },
  { upto: 360000, rate: 0.02 },
  { upto: 1000000, rate: 0.03 },
  { upto: 1500000, rate: 0.04 },
  { upto: 3000000, rate: 0.05 },
  { upto: Infinity, rate: 0.06 },
];

export function computeBSD(price) {
  const P = clean(price);
  let duty = 0;
  let prev = 0;
  for (const tier of BSD_TIERS) {
    if (P <= prev) break;
    duty += (Math.min(P, tier.upto) - prev) * tier.rate;
    prev = tier.upto;
  }
  return duty;
}

export const ABSD_PROFILES = [
  { id: "sc1", label: "Singapore Citizen — first property", rate: 0 },
  { id: "sc2", label: "Singapore Citizen — second property", rate: 0.2 },
  { id: "sc3", label: "Singapore Citizen — third & subsequent", rate: 0.3 },
  { id: "pr1", label: "Permanent Resident — first property", rate: 0.05 },
  { id: "pr2", label: "Permanent Resident — second property", rate: 0.3 },
  { id: "pr3", label: "Permanent Resident — third & subsequent", rate: 0.35 },
  { id: "fr", label: "Foreigner — any property", rate: 0.6 },
  { id: "entity", label: "Entity / Trust", rate: 0.65 },
];

export function ltvRule(outstandingLoans, tenure, age) {
  const loans = Math.min(Math.max(Math.round(Number(outstandingLoans) || 0), 0), 2);
  const longTenure = tenure > 30 || age + tenure > 65;
  let ltv;
  let minCashPct;
  if (loans === 0) {
    ltv = longTenure ? 0.55 : 0.75;
    minCashPct = longTenure ? 0.1 : 0.05;
  } else if (loans === 1) {
    ltv = longTenure ? 0.25 : 0.45;
    minCashPct = 0.25;
  } else {
    ltv = longTenure ? 0.15 : 0.35;
    minCashPct = 0.25;
  }
  return { ltv, minCashPct, longTenure, loans };
}

export function computeAffordability(input) {
  const income = clean(input.income);
  const coIncome = clean(input.coIncome);
  const debts = clean(input.debts);
  const housingPayments = clean(input.housingPayments);
  const cash = clean(input.cash);
  const cpf = clean(input.cpf);
  const actualRate = clean(input.actualRate);
  const otherCosts = clean(input.otherCosts ?? 3500);
  const age = Math.min(Math.max(Math.round(Number(input.age) || 0), 21), 70);
  const profile = ABSD_PROFILES.find((p) => p.id === input.profileId) || ABSD_PROFILES[0];
  const loans = Math.min(Math.max(Math.round(Number(input.outstandingLoans) || 0), 0), 2);

  const maxTenureByAge = Math.max(MAX_LOAN_AGE - age, 5);
  const tenure = Math.min(Math.max(Math.round(Number(input.tenure) || 25), 5), MAX_TENURE_YEARS, maxTenureByAge);
  const stressRate = Math.max(STRESS_RATE_FLOOR_PCT, actualRate);

  const grossMonthly = income + coIncome;
  const tdsrCeiling = TDSR_LIMIT * grossMonthly;
  const monthlyCapacity = Math.max(tdsrCeiling - debts - housingPayments, 0);
  const maxLoanByTdsr = loanFromPayment(monthlyCapacity, stressRate, tenure);
  const { ltv, minCashPct, longTenure } = ltvRule(loans, tenure, age);
  const funds = cash + cpf;

  const feasible = (P) => {
    const loan = Math.min(ltv * P, maxLoanByTdsr);
    const upfront = P - loan + computeBSD(P) + P * profile.rate + otherCosts;
    return upfront <= funds + 1e-6 && minCashPct * P <= cash + 1e-6;
  };
  let lo = 0;
  let hi = 100000000;
  for (let i = 0; i < 90; i++) {
    const mid = (lo + hi) / 2;
    if (feasible(mid)) lo = mid;
    else hi = mid;
  }
  const maxPrice = Math.floor(lo / 1000) * 1000;

  const maxLoan = Math.min(ltv * maxPrice, maxLoanByTdsr);
  const downpayment = maxPrice - maxLoan;
  const bsd = computeBSD(maxPrice);
  const absd = maxPrice * profile.rate;
  const upfrontTotal = downpayment + bsd + absd + otherCosts;
  const minCash = minCashPct * maxPrice;
  const monthlyActual = monthlyPayment(maxLoan, actualRate, tenure);
  const monthlyStress = monthlyPayment(maxLoan, stressRate, tenure);

  const fundsBinding = upfrontTotal >= funds * 0.995 || minCash >= cash * 0.995;
  const tdsrBinding = maxLoanByTdsr < ltv * maxPrice - 1;
  const binding = maxPrice <= 0 || fundsBinding ? "funds" : tdsrBinding ? "tdsr" : "ltv";

  const warnings = [];
  if (grossMonthly === 0) warnings.push("Enter a household income to compute borrowing capacity.");
  if (monthlyCapacity === 0 && grossMonthly > 0) warnings.push("Existing obligations already reach the 55% TDSR ceiling — no further borrowing capacity until debts are reduced.");
  if (maxPrice > 0 && maxPrice < 500000) warnings.push("Available funds are the main constraint — the budget shown is limited by cash/CPF on hand, not borrowing power.");
  if (longTenure) warnings.push("A longer tenure or loan running past age 65 reduces the maximum LTV (regulatory rule).");
  if (loans > 0) warnings.push("Existing housing loan(s) reduce the LTV limit and raise the minimum cash downpayment to 25%.");
  if (profile.rate > 0) warnings.push(`ABSD at ${(profile.rate * 100).toFixed(0)}% is included for the selected buyer profile (rates effective 27 Apr 2023).`);
  if (input.tenure && Number(input.tenure) > tenure) warnings.push(`Tenure capped at ${tenure} years (35-year maximum for private property and the age-${MAX_LOAN_AGE} rule).`);

  const assumptions = [
    "Private residential (landed) property purchased with a bank loan — HDB MSR rules are not applied.",
    `TDSR ceiling: 55% of gross monthly income across all debt obligations (MAS, OTP on/after 16 Dec 2021).`,
    `Loan eligibility assessed at the higher of the 4% regulatory medium-term floor or your actual rate — ${stressRate.toFixed(2)}% used here.`,
    `LTV ${(ltv * 100).toFixed(0)}% applied (${loans === 0 ? "first" : loans === 1 ? "second" : "third or subsequent"} housing loan${longTenure ? ", reduced band: tenure > 30 yrs or past age 65" : ""}).`,
    `Minimum cash downpayment ${(minCashPct * 100).toFixed(0)}%; the balance of the downpayment may come from CPF OA or cash.`,
    "BSD tiers effective 15 Feb 2023; ABSD effective 27 Apr 2023 (iras.gov.sg).",
    "CPF usage subject to CPF Board eligibility and limits — not all OA balances may be usable.",
    `Estimated ${otherCosts.toLocaleString("en-SG", { style: "currency", currency: "SGD", maximumFractionDigits: 0 })} for legal and other upfront costs (editable).`,
  ];

  return {
    grossMonthly, tdsrCeiling, monthlyCapacity, maxLoanByTdsr,
    tenure, stressRate, actualRate, ltv, minCashPct, longTenure,
    profile, funds, otherCosts,
    maxPrice, maxLoan, downpayment, minCash, bsd, absd, upfrontTotal,
    monthlyActual, monthlyStress, binding, warnings, assumptions,
  };
}
